/**
 * ★★ I18N-01 — QUELLE LANGUE CET ÉCRAN PARLE, ET QUI L'A DÉCIDÉ.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LES DEUX PORTEURS, ET LEUR ORDRE DE PRIORITÉ
 * ────────────────────────────────────────────────────────────────────────────
 *
 * L'arbitrage du 04/09/2026 (voir `@tb/shared/langues.ts`) donne deux porteurs
 * et un ordre. Cet ordre est tout le contenu de ce fichier :
 *
 *  1. **le choix explicite du visiteur**, s'il en a fait un. Il vit dans son
 *     `localStorage`, donc dans son navigateur à lui, et ne touche jamais une
 *     donnée du théâtre ;
 *  2. **la langue du théâtre**, servie par `GET /theatre/publique` — le défaut
 *     de la maison ;
 *  3. **la langue du navigateur**, ramenée à l'une des trois servies ;
 *  4. **le français**, qui n'est jamais atteint sauf accident.
 *
 * ★ **Sur la vitrine, l'étape 2 n'existe pas** : l'apex ne dessert aucun
 *   théâtre, `useTheatre()` y retombe sur ses défauts. Le visiteur et son
 *   navigateur décident donc seuls, ce qui est exactement ce qu'on veut d'une
 *   page qui s'adresse à un prospect dont on ne sait rien.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ POURQUOI LE CHOIX DU VISITEUR NE REMONTE PAS AU SERVEUR
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Parce que ce n'est pas une donnée du théâtre. Un spectateur qui lit la
 * boutique en allemand n'a pas changé la langue de la maison : il a changé
 * **son affichage**. Écrire sa préférence en base demanderait une session
 * (qu'il n'a pas), un consentement (pour une préférence anodine) et une purge
 * (pour un enregistrement dont personne ne veut). Le `localStorage` est le bon
 * support : local, révocable par l'utilisateur, invisible du serveur.
 *
 * ⚠️ **Conséquence assumée, et c'est la même que pour les documents :** les
 *    billets et factures d'un spectateur partent dans la langue DU THÉÂTRE,
 *    parce que le serveur qui les compose n'a jamais vu ce `localStorage`.
 *    Un spectateur germanophone chez un théâtre romand lit la boutique en
 *    allemand et reçoit ses billets en français. Ce n'est pas une régression à
 *    signaler : c'est le périmètre arbitré.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LE STOCKAGE PEUT ÊTRE INDISPONIBLE, ET CE N'EST PAS UN CAS RARE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `localStorage` lève en navigation privée sur certains navigateurs, quand les
 * données de site sont bloquées, et dans un cadre embarqué. Toutes les lectures
 * et écritures sont donc gardées : une préférence qu'on ne peut pas retenir
 * vaut mieux qu'un écran blanc.
 */

import { useCallback, useSyncExternalStore } from 'react'

import { LANGUE_DEFAUT, versLangue, type Langue, type Traduit } from '@tb/shared'

import { useTheatre } from './theatre'

/** La clé du choix explicite, dans le stockage du navigateur. */
export const CLE_LANGUE = 'tb.langue'

/* -------------------------------------------------------------------------- */
/* 1. La préférence du visiteur — un petit magasin observable                 */
/* -------------------------------------------------------------------------- */

/**
 * Les abonnés au changement de langue.
 *
 * `useSyncExternalStore` a besoin d'une source d'abonnement : sans elle, un
 * changement de langue dans le sélecteur ne redessinerait que le sélecteur, et
 * la page resterait dans l'ancienne langue jusqu'au prochain rendu — le défaut
 * classique d'une préférence rangée dans un module.
 */
const abonnes = new Set<() => void>()

function souscrire(rappel: () => void): () => void {
  abonnes.add(rappel)
  /* `storage` ne se déclenche que dans les AUTRES onglets : c'est ce qui fait
     suivre la langue quand quelqu'un ouvre la boutique en double. */
  const surStockage = (evenement: StorageEvent): void => {
    if (evenement.key === CLE_LANGUE) rappel()
  }
  globalThis.addEventListener?.('storage', surStockage)
  return () => {
    abonnes.delete(rappel)
    globalThis.removeEventListener?.('storage', surStockage)
  }
}

/** Le choix explicite du visiteur, ou `null` s'il n'en a pas fait. */
export function languePreferee(): Langue | null {
  try {
    const brut = globalThis.localStorage?.getItem(CLE_LANGUE)
    if (brut === null || brut === undefined) return null
    const langue = versLangue(brut, LANGUE_DEFAUT)
    /* `versLangue` retombe sur le défaut : une valeur illisible ne doit pas
       se faire passer pour un choix délibéré de français. */
    return brut.toLowerCase().startsWith(langue) ? langue : null
  } catch {
    return null
  }
}

function lireInstantane(): string {
  return languePreferee() ?? ''
}

/** Le même instantané côté serveur — le rendu statique n'a pas de stockage. */
function lireInstantaneServeur(): string {
  return ''
}

/**
 * Pose (ou retire) le choix du visiteur, et prévient toute la page.
 *
 * `null` efface la préférence : l'écran retombe alors sur la langue du théâtre,
 * ce qui est le comportement attendu d'un « suivre le théâtre » explicite.
 */
export function choisirLangue(langue: Langue | null): void {
  try {
    if (langue === null) globalThis.localStorage?.removeItem(CLE_LANGUE)
    else globalThis.localStorage?.setItem(CLE_LANGUE, langue)
  } catch {
    /* Stockage indisponible : la préférence ne survivra pas au rechargement,
       mais elle doit s'appliquer TOUT DE SUITE — d'où la notification qui
       suit, hors du `try`. */
  }
  for (const rappel of abonnes) rappel()
}

/* -------------------------------------------------------------------------- */
/* 2. La langue effective                                                     */
/* -------------------------------------------------------------------------- */

/** La langue du navigateur, ramenée à l'une des trois servies, ou `null`. */
function langueDuNavigateur(): Langue | null {
  const brut = globalThis.navigator?.language
  if (brut === undefined) return null
  const langue = versLangue(brut, LANGUE_DEFAUT)
  return brut.toLowerCase().startsWith(langue) ? langue : null
}

/**
 * La langue que cet écran doit parler, tous porteurs arbitrés.
 *
 * ★ `actif = false` sur la vitrine : elle n'appartient à aucun théâtre et ne
 *   doit pas appeler `GET /theatre/publique` (l'apex répondrait 404 et le
 *   journal se remplirait d'erreurs qui n'en sont pas).
 */
/**
 * ★★ DEUX HOOKS, ET NON UN SEUL AVEC UN DRAPEAU — corrigé le 05/09.
 *
 * La première version prenait `actif = true` et appelait `useTheatre(actif)`
 * dans tous les cas, en comptant sur `enabled: false` pour ne pas partir en
 * réseau. **Elle plantait sur la vitrine hors application** : `useQuery` exige
 * un `QueryClientProvider` même désactivé, et les tests qui rendent
 * `<PageVitrine />` seule — c'est-à-dire tout le harnais de garde-fous — se
 * sont arrêtés net.
 *
 * Le drapeau était de toute façon un aveu : une page qui n'appartient à aucun
 * théâtre n'a pas à porter un paramètre disant qu'elle n'en a pas. Les règles
 * des hooks interdisant l'appel conditionnel, la réponse est deux fonctions.
 */

/**
 * La langue d'une surface de THÉÂTRE — back-office, boutique, portail.
 * Arbitrage complet : visiteur, puis théâtre, puis navigateur.
 */
export function useLangue(): Langue {
  const preferee = useSyncExternalStore(souscrire, lireInstantane, lireInstantaneServeur)
  const theatre = useTheatre()
  if (preferee !== '') return preferee as Langue
  if (theatre.langue !== null) return theatre.langue
  return langueDuNavigateur() ?? LANGUE_DEFAUT
}

/**
 * La langue de la VITRINE, qui vit sur l'apex et ne dessert aucun théâtre.
 *
 * ★ N'appelle ni `useTheatre()` ni la moindre requête : le visiteur et son
 *   navigateur décident seuls, ce qui est exactement ce qu'on veut d'une page
 *   qui s'adresse à un prospect dont on ne sait rien.
 */
export function useLangueVitrine(): Langue {
  const preferee = useSyncExternalStore(souscrire, lireInstantane, lireInstantaneServeur)
  if (preferee !== '') return preferee as Langue
  return langueDuNavigateur() ?? LANGUE_DEFAUT
}

/**
 * `true` si l'affichage suit un choix du visiteur plutôt que le théâtre.
 * Le sélecteur s'en sert pour proposer « revenir à la langue du théâtre ».
 */
export function useLangueSurchargee(): boolean {
  return useSyncExternalStore(souscrire, lireInstantane, lireInstantaneServeur) !== ''
}

/* -------------------------------------------------------------------------- */
/* 3. La langue COURANTE, pour le code qui n'est pas un composant             */
/* -------------------------------------------------------------------------- */

/**
 * ★★ LE PONT VERS `lib/format.ts`, ET POURQUOI IL EXISTE.
 *
 * Soixante-six fichiers appellent `formatJourLong()` et consorts. Leur faire
 * passer la langue en argument aurait voulu dire toucher soixante-six fichiers
 * et leurs tests, pour une donnée qui est la même sur toute la page — et
 * garantir qu'un seul appel oublié afficherait « jeudi 24 septembre » au
 * milieu d'un écran allemand.
 *
 * La langue est donc lisible **hors composant**, depuis ce module. Ce n'est
 * pas un état global caché : c'est la même source que `useLangue()`, exposée
 * sous une forme que du code non-React peut lire.
 *
 * ⚠️ **Un seul point d'écriture** : `enregistrerLangueDuTheatre()`, appelé une
 *    fois par la coque de l'application. Le laisser appeler d'ailleurs
 *    rouvrirait exactement le défaut que ce fichier ferme.
 */
let langueDuTheatreConnue: Langue | null = null

/**
 * Mémorise la langue du théâtre courant pour le code hors composant.
 *
 * Appelée par la coque quand `GET /theatre/publique` a répondu. Notifie les
 * abonnés : les écrans déjà dessinés avec une date française la redessinent.
 */
export function enregistrerLangueDuTheatre(langue: Langue | null): void {
  if (langueDuTheatreConnue === langue) return
  langueDuTheatreConnue = langue
  for (const rappel of abonnes) rappel()
}

/**
 * La langue effective, hors composant. Même arbitrage que `useLangue()` :
 * choix du visiteur, puis théâtre, puis navigateur, puis français.
 */
export function langueCourante(): Langue {
  return languePreferee() ?? langueDuTheatreConnue ?? langueDuNavigateur() ?? LANGUE_DEFAUT
}

/* -------------------------------------------------------------------------- */
/* 4. Traduire                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Le traducteur de l'écran courant.
 *
 * ```tsx
 * const t = useTraduire()
 * <h1>{t({ fr: 'Vos billets', de: 'Ihre Tickets', en: 'Your tickets' })}</h1>
 * ```
 *
 * ★★ **Le dictionnaire est écrit au point d'usage, et c'est délibéré.** Un
 *    fichier `de.json` séparé se désynchronise : on supprime une phrase de
 *    l'écran et la clé reste, on ajoute une phrase et la clé manque — et rien
 *    ne le dit avant la production. Ici `Traduit<T>` est un `Record` complet :
 *    **une langue oubliée ne compile pas**, et une phrase supprimée emporte
 *    ses trois traductions avec elle.
 *
 *    Le prix est que les trois langues cohabitent dans le JSX. Pour les longs
 *    textes — la vitrine, l'aide — on range le dictionnaire dans un module
 *    voisin (`textes.ts`) et le JSX n'appelle que `t(TEXTES.accroche)`.
 */
export function useTraduire(): <T>(dictionnaire: Traduit<T>) => T {
  const langue = useLangue()
  return useCallback(<T,>(dictionnaire: Traduit<T>): T => dictionnaire[langue], [langue])
}

/** Le traducteur de la vitrine — sans théâtre, donc sans requête. */
export function useTraduireVitrine(): <T>(dictionnaire: Traduit<T>) => T {
  const langue = useLangueVitrine()
  return useCallback(<T,>(dictionnaire: Traduit<T>): T => dictionnaire[langue], [langue])
}
